import { SxProps, Theme, ThemeProvider } from "@mui/material";
import { DataGridPro, GridColDef, GridRowsProp, GridSortModel } from "@mui/x-data-grid-pro";
import { ElementType, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { resetTableState, setInitialFetchingItems, setTableType } from "@store/table/table-slice";
import { selectedRowIdsSelector, updatingItemsSelector } from "@store/table/table-selector";
import { FaroTableToolbar } from "@components/common/faro-table/faro-table-toolbar";
import { FaroCustomRow } from "@components/common/faro-table/faro-custom-row";
import { FaroCustomCheckbox } from "@components/common/faro-table/faro-custom-checkbox";
import UpArrow from "@assets/icons/new/arrow-up_10px.svg?react";
import DownArrow from "@assets/icons/new/arrow-down_10px.svg?react";
import { EMPTY_PAGE_HEIGHT, EmptyPage } from "@components/common/empty-page/empty-page";
import { FaroPaginatedTableFooter } from "@components/common/faro-table/faro-paginated-table-footer";
import { BulkButtonComponents } from "@components/common/faro-table/faro-table-types";
import { Stack } from "@mui/material";
import { DEFAULT_HEADER_HEIGHT_IN_PX, FARO_TABLE_THEME } from "@components/common/faro-table/faro-table";
import { SidePanelContent } from "@components/sidepanel/side-panel-content";
import { TableEvents, TableType } from "@utils/track-event/track-event-list";
import { getLastPage } from "@utils/pagination-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface FaroPaginatedTableProps<T> {
  /** All entities to be shown in the table */
  entities: T[];
    
  /** The table columns */
  columns: GridColDef[];
  
  /** Whether data is loading */
  isLoading: boolean;
  
  /** Function to create a row object with id and entity from an entity */
  mapEntityToRow: (entity: T) => { id: string | number; entity: T };
  
  /** The number of items per page */
  itemsPerPage: number;
  
  /** Whether there are more items to load */
  hasMoreItems?: boolean;
  
  /** Callback for loading more items */
  loadMoreItems?: () => Promise<void>;
  
  /** Current page index */
  page?: number;
  
  /** Callback to set the current page */
  setPage?: (newPage: number) => void;
  
  /** Optional function to handle sort model changes */
  onSortModelChange?: (model: GridSortModel) => void;
  
  /** Bulk action buttons to show in toolbar */
  bulkActionButtons?: BulkButtonComponents;
  
  /** Whether to hide checkbox on each row */
  shouldHideCheckbox?: boolean;
  
  /** Icon to show in empty state */
  emptyIcon: ElementType;
  
  /** Title to show in empty state */
  emptyTitle?: string;
  
  /** Subtitle to show in empty state */
  emptySubtitle?: string;
  
  /** The component to render in the side panel */
  sidePanelContent?: ReactNode;
  
  /** The size of the side panel */
  sidePanelContentWidth?: string;
  
  /** Whether the side panel is opened */
  isSidePanelOpened?: boolean;
  
  /** The table type for Redux state */
  tableType: TableType;
  
  /** Custom styles for the DataGrid */
  sx?: SxProps<Theme>;
  
  /** Callback when a row is clicked */
  onRowClick?: (id: string | number) => void;
}

/**
 * A reusable paginated table component that handles server-side pagination, sorting, and filtering.
 */
export function FaroPaginatedTable<T>({
  entities,
  columns,
  isLoading,
  mapEntityToRow,
  itemsPerPage,
  hasMoreItems = false,
  loadMoreItems,
  page = 0,
  setPage,
  onSortModelChange,
  bulkActionButtons,
  shouldHideCheckbox = false,
  emptyIcon,
  emptyTitle = "No items to show",
  emptySubtitle = "",
  sidePanelContent,
  sidePanelContentWidth,
  isSidePanelOpened,
  tableType,
  sx,
  onRowClick,
}: FaroPaginatedTableProps<T>): JSX.Element {
  const dispatch = useAppDispatch();
  const updatingItems = useAppSelector(updatingItemsSelector);
  const selectedRowIds = useAppSelector(selectedRowIdsSelector);
  const { trackEvent } = useTrackEvent();

  const [internalPage, setInternalPage] = useState<number>(page);
  const [isFetchingMoreItems, setIsFetchingMoreItems] = useState<boolean>(false);
  
  // Use internal or external page state
  const currentPage = setPage ? page : internalPage;
  const setCurrentPage = setPage || setInternalPage;
  
  // Reset table state when component mounts
  useEffect(() => {
    dispatch(resetTableState());
    dispatch(setTableType(tableType));
    
    return () => {
      dispatch(resetTableState());
    };
  }, [dispatch, tableType]);
  
  // Compute start index based on current page
  const start = currentPage * itemsPerPage;
  
  const currentPageEntities = useMemo(() => {
   
    const end = start + itemsPerPage;
    return entities.slice(start, end);
  }, [entities, start, itemsPerPage]);
  
  // Calculate "to" value for footer display
  const to = start + currentPageEntities.length;
  
  // Total row count, set to -1 when there are more items to load
  const rowCount = hasMoreItems ? -1 : entities.length;
  
  // Calculate last page
  const lastPage = useMemo(() => getLastPage(rowCount, itemsPerPage), [rowCount, itemsPerPage]);
  
  // Create rows for the DataGrid
  const rows: GridRowsProp = useMemo(() => {
    return currentPageEntities.map(mapEntityToRow);
  }, [currentPageEntities, mapEntityToRow]);
  
  /**
   * Handle load more items when the next page button is clicked
   * or when the user scrolls to the bottom of the table
   */
  const handleLoadMoreItems = useCallback(async () => {
    if (hasMoreItems && loadMoreItems) {
      setIsFetchingMoreItems(true);
      
      await loadMoreItems();
      
      setIsFetchingMoreItems(false);
    }
  }, [hasMoreItems, loadMoreItems]);
  
  /**
   * Handle page change for pagination.
   * If navigating to the next page and more items need to be loaded,
   * it will load them first.
   */
  const handlePageChange = useCallback((newPage: number) => {
    // Tracking the page change event
    trackEvent({
      name: TableEvents.changePage,
      props: {
        page: currentPage,
        newPage,
        tableType,
      },
    });
    // Load more items if navigating to a next page and more items are available
    if (newPage > currentPage && hasMoreItems) {
      void handleLoadMoreItems();
    }
    
    setCurrentPage(newPage);
  }, [trackEvent, currentPage, tableType, hasMoreItems, setCurrentPage, handleLoadMoreItems]);
  
  // Compute loading state including fetching more items status
  const isLoadingState = useMemo(() => {
    return isLoading || isFetchingMoreItems;
  }, [isLoading, isFetchingMoreItems]);
  
  return (
    <ThemeProvider theme={FARO_TABLE_THEME}>
      {bulkActionButtons && (
        <FaroTableToolbar
          numberOfSelectedRows={updatingItems.length}
          numberOfRows={entities.length}
          actionButtons={bulkActionButtons}
        />
      )}
      <Stack flexDirection="row" gap={sidePanelContent && isSidePanelOpened ? "20px" : 0}>
        <DataGridPro
          sx={{
            "& .MuiDataGrid-row": {
              cursor: "default",
            },
            // Data grid component has a default overlay height that is too short to display the "no rows" component.
            // Set the overlay height to the height of the "no rows" component:
            // https://mui.com/x/react-data-grid/layout/#overlay-height
            // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
            "--DataGrid-overlayHeight": EMPTY_PAGE_HEIGHT,
            ...sx,
          }}
          rows={rows}
          columns={columns}
          slots={{
            row: FaroCustomRow,
            baseCheckbox: FaroCustomCheckbox,
            columnSortedAscendingIcon: UpArrow,
            columnSortedDescendingIcon: DownArrow,
            footer: () => !isLoadingState ? (
              <FaroPaginatedTableFooter
                from={start + 1}
                to={to}
                rowCount={rowCount}
                page={currentPage}
                lastPage={lastPage}
                isLoading={isLoadingState}
                handlePageChange={handlePageChange}
              />
            ) : null,
            noRowsOverlay: () => <EmptyPage title={emptyTitle} subtitle={emptySubtitle} icon={emptyIcon} />,
          }}
          slotProps={{
            panel: {
              sx: {
                "& .MuiDataGrid-columnsPanel": {
                  height: "auto",
                },
              },
            },
          }}
          disableColumnFilter
          disableColumnMenu
          disableRowSelectionOnClick
          checkboxSelection={!shouldHideCheckbox}
          loading={isLoadingState}
          columnHeaderHeight={DEFAULT_HEADER_HEIGHT_IN_PX}
          rowHeight={70}
          onRowSelectionModelChange={(newSelectedRowIds) => {
            dispatch(setInitialFetchingItems([...newSelectedRowIds]));
          }}
          onRowClick={onRowClick ? (params) => onRowClick(params.id) : undefined}
          rowSelectionModel={selectedRowIds}
          onSortModelChange={onSortModelChange}
          
          // Server side pagination attributes
          pageSizeOptions={[itemsPerPage]}
          pagination
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{ page: currentPage, pageSize: itemsPerPage }}
          keepNonExistentRowsSelected
        />
      
        {sidePanelContent && (
          <SidePanelContent
            isOpened={isSidePanelOpened}
            contentWidth={sidePanelContentWidth}
            sx={{ mt: "16px" }}
          >
            {sidePanelContent}
          </SidePanelContent>
        )}
      </Stack>
    </ThemeProvider>
  );
}